import { useEffect, useState } from "react";
import { Plus, Trash2, Send, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money, waLink, waNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const FILM = /glossy|gloss|matte|x250|x 250/i;
const API = process.env.REACT_APP_BACKEND_URL;

export const SendQuoteDialog = ({ open, onOpenChange, job }) => {
  const [items, setItems] = useState([{ service: "", price: "" }]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastPdf, setLastPdf] = useState(null);

  useEffect(() => {
    if (open) {
      const base = (job?.services || []).length
        ? job.services.map((s) => ({ service: s, price: "" }))
        : [{ service: "", price: "" }];
      setItems(base);
      setNotes("");
      setLastPdf(null);
      // auto-fill prices from the system price list
      base.forEach((row, i) => { if (FILM.test(row.service)) autoPrice(i, row.service); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const autoPrice = async (idx, service) => {
    if (!FILM.test(service || "")) return;
    try {
      const { data } = await api.get("/pricing/auto", {
        params: { make: job?.vehicle?.make || "", model: job?.vehicle?.model || "", service,
          ...(job?.ppf_sqft ? { sqft: Number(job.ppf_sqft) } : {}) },
      });
      if (data?.price != null)
        setItems((its) => its.map((r, i) => (i === idx && !r.price ? { ...r, price: String(data.price) } : r)));
    } catch { /* ignore */ }
  };

  const setRow = (i, patch) => setItems((its) => its.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setItems((its) => [...its, { service: "", price: "" }]);
  const removeRow = (i) => setItems((its) => its.filter((_, idx) => idx !== i));
  const total = items.reduce((s, r) => s + (Number(r.price) || 0), 0);

  const send = async () => {
    const clean = items.filter((r) => r.service.trim());
    if (!clean.length) return toast.error("Add at least one service");
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/quote`, {
        items: clean.map((r) => ({ service: r.service.trim(), price: Number(r.price) || 0 })),
        notes,
      });
      const pdfUrl = `${API}${data.pdf_path}`;
      setLastPdf(pdfUrl);
      const phone = data.customer_phone || job?.customer_phone || "";
      const lines = clean.map((r) => `• ${r.service.trim()} — ${money(Number(r.price) || 0)}`).join("\n");
      const msg =
        `Hi ${data.customer_name || ""}, here is your Veloskin quote (${data.number}):\n\n` +
        `${lines}\n\nTotal: ${money(data.total)}\nValid until ${data.valid_until_date}\n\n` +
        `Full quote PDF: ${pdfUrl}`;
      const link = phone
        ? `${waLink(phone)}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(link, "_blank");
      toast.success("Quote saved — your WhatsApp is opening");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="send-quote-dialog">
        <DialogHeader>
          <DialogTitle>Send Quote</DialogTitle>
          <DialogDescription>
            Prices pre-fill from the price list — edit freely. Sends via your own WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {items.map((r, i) => (
            <div key={i} className="flex items-end gap-2" data-testid={`quote-row-${i}`}>
              <div className="flex-1">
                {i === 0 && <Label className="text-xs">Service</Label>}
                <Input
                  className="mt-1 min-h-[42px]"
                  data-testid={`quote-service-${i}`}
                  value={r.service}
                  placeholder="e.g. Veloskin Glossy"
                  onChange={(e) => setRow(i, { service: e.target.value })}
                  onBlur={() => autoPrice(i, r.service)}
                />
              </div>
              <div className="w-32">
                {i === 0 && <Label className="text-xs">Price (₹)</Label>}
                <Input
                  className="mt-1 min-h-[42px] text-right"
                  inputMode="decimal"
                  data-testid={`quote-price-${i}`}
                  value={r.price}
                  onChange={(e) => setRow(i, { price: e.target.value })}
                />
              </div>
              <Button
                variant="ghost" size="icon" className="mb-0.5 text-slate-400 hover:text-rose-600"
                data-testid={`quote-remove-${i}`}
                onClick={() => removeRow(i)} disabled={items.length === 1}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-1" data-testid="quote-add-service" onClick={addRow}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add service
          </Button>
        </div>

        <div>
          <Label className="text-xs">Note (optional)</Label>
          <Input className="mt-1" data-testid="quote-notes" value={notes}
            onChange={(e) => setNotes(e.target.value)} placeholder="Any note for the customer" />
        </div>

        <div className="flex items-center justify-between rounded-md bg-slate-50 px-4 py-2.5">
          <span className="text-sm text-slate-500">Total</span>
          <span className="text-lg font-bold text-brand-ink" data-testid="quote-total">{money(total)}</span>
        </div>

        {lastPdf && (
          <a href={lastPdf} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 text-sm text-brand underline" data-testid="quote-pdf-link">
            <FileText className="h-4 w-4" /> Open quote PDF
          </a>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button data-testid="quote-send-btn" disabled={busy} onClick={send}>
            <Send className="mr-2 h-4 w-4" /> Generate & open WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendQuoteDialog;
